import * as THREE from 'three';

/**
 * Scales a mesh by the given scale factors along the x, y, and z axes.
 * @param {THREE.Mesh} mesh - The mesh to be scaled.
 * @param {number} scaleX - The scale factor along the x-axis.
 * @param {number} scaleY - The scale factor along the y-axis.
 * @param {number} scaleZ - The scale factor along the z-axis.
 * @returns {THREE.Mesh} The scaled mesh.
 */
export const scaleMesh = (mesh, scaleX, scaleY, scaleZ) => {
  for (let i = 0; i < mesh.geometry.attributes.position.array.length; i+=3) {
    mesh.geometry.attributes.position.array[i] *= scaleX;
    mesh.geometry.attributes.position.array[i+1] *= scaleY;
    mesh.geometry.attributes.position.array[i+2] *= scaleZ;
  }

  mesh.geometry.attributes.position.needsUpdate = true;
  mesh.geometry.computeVertexNormals();

  return mesh;
}

/**
 * Scales the geometry by the specified factors.
 * @param {THREE.BufferGeometry} geometry - The geometry to be scaled.
 * @param {number} scaleX - The scaling factor along the x-axis.
 * @param {number} scaleY - The scaling factor along the y-axis.
 * @param {number} scaleZ - The scaling factor along the z-axis.
 * @returns {THREE.Geometry} The scaled geometry.
 */
export const scaleGeometry = (geometry, scaleX, scaleY, scaleZ) => {
  for (let i = 0; i < geometry.attributes.position.array.length; i+=3) {
    geometry.attributes.position.array[i] *= scaleX;
    geometry.attributes.position.array[i+1] *= scaleY;
    geometry.attributes.position.array[i+2] *= scaleZ;
  }

  geometry.attributes.position.needsUpdate = true;
  geometry.computeVertexNormals();

  return geometry;
}

/**
 * Gradually scales the geometry based on the provided offsets and compressions.
 * Only supports scaling in the 'y' direction.
 *
 * @param {THREE.BufferGeometry} geometry - The geometry to be scaled.
 * @param {number[]} offsets - The offsets for scaling in the 'y' direction.
 * @param {number[]} compressions - The compressions for scaling in the 'y' direction.
 * @param {string} [dir='y'] - The direction of scaling. Only 'y' is supported.
 * @returns {THREE.Geometry} The scaled geometry.
 */
export const gradualScaleGeometry = (geometry, offsets, compressions, dir='y') => {
  if (dir != 'y') {
    console.log('gradualScaleGeometry: only y direction supported');
    return;
  }

  const scaleVectors = [];
  for (let i = 0; i < offsets.length; i++) {
    scaleVectors.push(new THREE.Vector3(offsets[i], compressions[i], 0));
  }

  const scaleCurveVectors = new THREE.CatmullRomCurve3(scaleVectors).getPoints(1000);
  const refValue = scaleCurveVectors.map(v => v.x);
  const refScale = scaleCurveVectors.map(v => v.y);

  for (let i = 0; i < geometry.attributes.position.array.length; i+=3) {
    const y = geometry.attributes.position.array[i+1];
    const diff = refValue.map(v => Math.abs(v-y));
    const idx = diff.indexOf(Math.min(...diff));
    
    const scale = refScale[idx];

    geometry.attributes.position.array[i] *= scale/100; // x
    geometry.attributes.position.array[i+2] *= scale/100; // z
  }

  geometry.attributes.position.needsUpdate = true;
  geometry.computeVertexNormals();
  
  return geometry;
}

/**
 * Performs Laplacian smoothing on a geometry.
 * 
 * @param {THREE.BufferGeometry} geometry - The geometry to be smoothed.
 * @param {number} iterations - The number of smoothing iterations to perform.
 * @returns {THREE.BufferGeometry} - The smoothed geometry.
 */
export const laplacianSmooth = (geometry, iterations) => {
  const vertices = geometry.attributes.position.array;
  const vertexCount = vertices.length / 3;
  const adjacencies = new Array(vertexCount);

  // Initialize the adjacency list
  for (let i = 0; i < vertexCount; i++) {
    adjacencies[i] = [];
  }

  // Collect vertex adjacency information
  const faces = geometry.index ? geometry.index.array : undefined;
  if (faces) {
    for (let i = 0; i < faces.length; i += 3) {
      adjacencies[faces[i]].push(faces[i + 1], faces[i + 2]);
      adjacencies[faces[i + 1]].push(faces[i], faces[i + 2]);
      adjacencies[faces[i + 2]].push(faces[i], faces[i + 1]);
    }
  } else {
    // If there are no vertex indices, assume all vertices are adjacent
    for (let i = 0; i < vertexCount; i++) {
      for (let j = 0; j < vertexCount; j++) {
        if (i !== j) {
          adjacencies[i].push(j);
        }
      }
    }
  }

  // Perform smoothing iterations
  for (let iter = 0; iter < iterations; iter++) {
    const newVertices = new Float32Array(vertices.length);

    for (let i = 0; i < vertexCount; i++) {
      let avgX = 0, avgY = 0, avgZ = 0;
      const neighborCount = adjacencies[i].length;

      for (let j = 0; j < neighborCount; j++) {
        const neighborIdx = adjacencies[i][j];
        avgX += vertices[neighborIdx * 3];
        avgY += vertices[neighborIdx * 3 + 1];
        avgZ += vertices[neighborIdx * 3 + 2];
      }

      avgX /= neighborCount;
      avgY /= neighborCount;
      avgZ /= neighborCount;

      newVertices[i * 3] = avgX;
      newVertices[i * 3 + 1] = avgY;
      newVertices[i * 3 + 2] = avgZ;
    }

    // Replace geometry with updated vertex information
    geometry.attributes.position.array = newVertices;
    geometry.attributes.position.needsUpdate = true;
  }

  // Recalculate vertex normals
  geometry.computeVertexNormals();

  return geometry;
}

export const convertNonindexedToIndexed = (geometry) => {
  const vertices = geometry.attributes.position.array;
  const indices = [];
  for (let i = 0; i < vertices.length / 3; i++) {
    indices.push(i);
  }

  geometry.setIndex(indices);
  geometry.index.needsUpdate = true;

  return geometry;
}

export const flipNormals = (geometry) => {
  if (geometry.index === null) {
    geometry = convertNonindexedToIndexed(geometry);
  }

  const faceIndices = geometry.index;
  for (let i = 0; i < faceIndices.array.length; i += 3) {
    const tmp = faceIndices.array[i];
    faceIndices.array[i] = faceIndices.array[i + 2];
    faceIndices.array[i + 2] = tmp;
  }
  geometry.setIndex(faceIndices);
  geometry.index.needsUpdate = true;

  return geometry;
}

/**
 * Returns an array of open edge points for a given mesh socket.
 * @param {THREE.Mesh} meshSocket - The mesh socket to find open edge points for.
 * @returns {Array<THREE.Vector3>} An array of open edge points.
 */
export const getOpenEdgePoints = (meshSocket) => {
  /**
   * Finds open edges in a given geometry.
   * @param {THREE.BufferGeometry} geometry - The geometry to search for open edges.
   * @param {number} [epsilon=1e-4] - The tolerance value for comparing floating point numbers.
   * @returns {Array<Array<number>>} An array of open edges, where each edge is represented by an array of two vertex indices.
   */
  const findOpenEdges = (geometry, epsilon = 1e-4) => {
    const edges = new Map();
    const faceIndices = geometry.index.array;
  
    for (let i = 0; i < faceIndices.length; i += 3) {
      for (let j = 0; j < 3; j++) {
        const a = faceIndices[i + j];
        const b = faceIndices[i + ((j + 1) % 3)];
        const key = `${Math.min(a, b)}_${Math.max(a, b)}`;
  
        if (edges.has(key)) {
          edges.set(key, edges.get(key) + 1);
        } else {
          edges.set(key, 1);
        }
      }
    }
  
    const openEdges = [];
    edges.forEach((count, edge) => {
      if (count === 1) {
        const vertices = edge.split('_').map(a => parseInt(a));
        openEdges.push(vertices);
      }
    });
  
    return openEdges;
  }
  
  const openEdges = findOpenEdges(meshSocket.geometry);
  // TODO: need to modify. This is not always true
  const firstIdx = openEdges[0][0];
  const lastIdx = openEdges[openEdges.length-1][1];
  // connect open edges
  const openEdgePoints = [];
  for (let i = firstIdx; i < lastIdx+1; i++) {
    openEdgePoints.push(new THREE.Vector3(
      meshSocket.geometry.attributes.position.array[i*3],
      meshSocket.geometry.attributes.position.array[i*3+1],
      meshSocket.geometry.attributes.position.array[i*3+2]
    ));
  }
  openEdgePoints.push(openEdgePoints[0].clone()); // close the loop

  return [openEdgePoints, openEdges];
}